import { LDFlagSet, useFlags, withLDProvider } from 'launchdarkly-react-client-sdk';
import { ComponentType, PropsWithChildren } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Maintenance } from '@brighte/form-components';
import { Footer } from '@components/Footer';
import { datadogRum } from '@datadog/browser-rum';
import { OpenBankingStepsProvider } from '@provider';
import { Container } from '@spark-web/container';
import { Stack } from '@spark-web/stack';
import ApplicationForm from './Form';
import { ApplicationAssistant, AssistantProvider } from './components/Assistant';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import { useDatadogAddError } from './hooks';
import { StepperProvider } from './provider/StepperProvider';
import { ENV_VARIABLES } from './utils/constants';

const initialContext = {
  kind: 'multi',
  user: {
    key: 'anonymous',
  },
};

enum FeatureFlagKeys {
  Maintenance = 'maintenance-page-customer-app',
  HideJointApplicantOption = 'hide-joint-applicant-option',
  EnableBnplIncomeVerificationViaCdr = 'income-verification-with-cdr-option',
  CdrIncomeVerificationAbTesting = 'cdr-income-verification-ab-testing',
  IncomeVerificationFeedback = 'income-verification-feedback',
  BrightePayIncomeOccupationGate = 'brightepay-income-occupation-gate',
  EnableEligibilityCheckForm = 'enableEligiblityCheckForm',
  RedirectCustomerToCustomerPortal = 'redirect-customer-to-customer-portal',
  EnableApplicationAssistant = 'enable-application-assistant',
}

interface FeatureFlag extends LDFlagSet {
  [FeatureFlagKeys.Maintenance]: { enableMaintenance: boolean; endDate?: string; endTime?: string };
  [FeatureFlagKeys.HideJointApplicantOption]: boolean;
  [FeatureFlagKeys.EnableBnplIncomeVerificationViaCdr]: boolean;
  [FeatureFlagKeys.CdrIncomeVerificationAbTesting]: boolean;
  [FeatureFlagKeys.IncomeVerificationFeedback]: boolean;
  [FeatureFlagKeys.BrightePayIncomeOccupationGate]: boolean;
  [FeatureFlagKeys.EnableEligibilityCheckForm]: boolean;
  [FeatureFlagKeys.RedirectCustomerToCustomerPortal]: boolean;
  [FeatureFlagKeys.EnableApplicationAssistant]: boolean;
}

// The assistant must never take the form down with it: any crash inside is
// swallowed (and logged to Datadog) rather than surfaced to the customer.
const AssistantSilentFallback = () => null;

function IsolatedAssistant() {
  const { logError } = useDatadogAddError();
  return (
    <ErrorBoundary FallbackComponent={AssistantSilentFallback} onError={logError}>
      <ApplicationAssistant />
    </ErrorBoundary>
  );
}

function App() {
  // TO DO: this is calling graphql API -> we need to re-think this - its rerendering the app due to the error expected coming from first api call
  const { logError } = useDatadogAddError();
  const flag = useFlags<FeatureFlag>();

  const { enableMaintenance, endTime, endDate } = flag[FeatureFlagKeys.Maintenance];

  return (
    <Container size="small">
      <Stack gap="xxlarge" padding={{ mobile: 'medium', desktop: 'xlarge' }}>
        <Maintenance enabled={enableMaintenance} endTime={endTime} endDate={endDate}>
          <OpenBankingStepsProvider>
            <StepperProvider>
              <AssistantProvider>
                <ErrorBoundary FallbackComponent={ErrorFallback} onError={logError}>
                  <ApplicationForm />
                  <Footer />
                </ErrorBoundary>
                <IsolatedAssistant />
              </AssistantProvider>
            </StepperProvider>
          </OpenBankingStepsProvider>
        </Maintenance>
      </Stack>
    </Container>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const e2eFlags = (window as any).__LD_TEST_FLAGS__;

// Dev-only: turn the assistant on when the dev server is started with the
// assistant forward harness (see src/test/handlers/assistant.ts). DEV guard
// means this is tree-shaken out of production builds.
const devAssistantFlags =
  import.meta.env.DEV && import.meta.env.VITE_ASSISTANT_DEV_URL
    ? { [FeatureFlagKeys.EnableApplicationAssistant]: true }
    : {};

const AppWithLD = withLDProvider({
  // * LaunchDarkly requires a client ID no matter what
  // * Fake ID is used to initialize the sdk for tests and local development

  clientSideID: ENV_VARIABLES.LAUNCH_DARKLY_CLIENT_ID ? ENV_VARIABLES.LAUNCH_DARKLY_CLIENT_ID : 'fake_id',
  context: initialContext,
  options: {
    bootstrap: e2eFlags || {
      [FeatureFlagKeys.Maintenance]: {
        enableMaintenance: false,
        endDate: '',
        endTime: '',
      },
      [FeatureFlagKeys.HideJointApplicantOption]: false,
      [FeatureFlagKeys.EnableBnplIncomeVerificationViaCdr]: false,
      [FeatureFlagKeys.CdrIncomeVerificationAbTesting]: false,
      [FeatureFlagKeys.IncomeVerificationFeedback]: false,
      [FeatureFlagKeys.BrightePayIncomeOccupationGate]: false,
      [FeatureFlagKeys.EnableEligibilityCheckForm]: true,
      [FeatureFlagKeys.RedirectCustomerToCustomerPortal]: false,
      [FeatureFlagKeys.EnableApplicationAssistant]: false,
      ...devAssistantFlags,
    },
    inspectors: [
      {
        type: 'flag-used',
        name: 'dd-inspector',
        method: (key, detail) => {
          datadogRum.addFeatureFlagEvaluation(key, detail.value);
        },
      },
    ],
  },
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  flags: e2eFlags || {
    [FeatureFlagKeys.Maintenance]: { enableMaintenance: false },
    [FeatureFlagKeys.HideJointApplicantOption]: false,
    [FeatureFlagKeys.EnableBnplIncomeVerificationViaCdr]: false,
    [FeatureFlagKeys.CdrIncomeVerificationAbTesting]: false,
    [FeatureFlagKeys.IncomeVerificationFeedback]: false,
    [FeatureFlagKeys.BrightePayIncomeOccupationGate]: false,
    [FeatureFlagKeys.EnableEligibilityCheckForm]: true,
    [FeatureFlagKeys.RedirectCustomerToCustomerPortal]: false,
    [FeatureFlagKeys.EnableApplicationAssistant]: false,
    ...devAssistantFlags,
  },
})(App as ComponentType<PropsWithChildren<unknown>>);

export { App, AppWithLD };
